/**
 * Deterministic 3D value noise + curl, for the turbulence module (plan §3.2:
 * "curl noise — CPU simplex on the sim side"). Value noise with smootherstep
 * interpolation is visually indistinguishable from simplex at the scales
 * stylized VFX uses, and needs no permutation-table state.
 */
function hash3(xi, yi, zi) {
    let h = (xi * 374761393 + yi * 668265263 + zi * 2147483647) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
}
const smoother = (t) => t * t * t * (t * (t * 6 - 15) + 10);
/** Smooth value noise in [-1, 1]. */
export function valueNoise3(x, y, z) {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const zi = Math.floor(z);
    const fx = smoother(x - xi);
    const fy = smoother(y - yi);
    const fz = smoother(z - zi);
    let result = 0;
    for (let dz = 0; dz <= 1; dz++) {
        for (let dy = 0; dy <= 1; dy++) {
            for (let dx = 0; dx <= 1; dx++) {
                const w = (dx ? fx : 1 - fx) * (dy ? fy : 1 - fy) * (dz ? fz : 1 - fz);
                result += w * hash3(xi + dx, yi + dy, zi + dz);
            }
        }
    }
    return result * 2 - 1;
}
const EPS = 0.1;
// Offsets decorrelate the three potential components.
const OFF = [0, 137.13, 291.77];
function potential(c, x, y, z) {
    return valueNoise3(x + OFF[c], y + OFF[c], z + OFF[c]);
}
/**
 * Curl of a noise potential field — divergence-free, so particles swirl
 * instead of clumping. Writes a vector with components roughly in [-1, 1].
 */
export function curlNoise3(x, y, z, out) {
    const dzdy = (potential(2, x, y + EPS, z) - potential(2, x, y - EPS, z)) / (2 * EPS);
    const dydz = (potential(1, x, y, z + EPS) - potential(1, x, y, z - EPS)) / (2 * EPS);
    const dxdz = (potential(0, x, y, z + EPS) - potential(0, x, y, z - EPS)) / (2 * EPS);
    const dzdx = (potential(2, x + EPS, y, z) - potential(2, x - EPS, y, z)) / (2 * EPS);
    const dydx = (potential(1, x + EPS, y, z) - potential(1, x - EPS, y, z)) / (2 * EPS);
    const dxdy = (potential(0, x, y + EPS, z) - potential(0, x, y - EPS, z)) / (2 * EPS);
    out[0] = dzdy - dydz;
    out[1] = dxdz - dzdx;
    out[2] = dydx - dxdy;
    return out;
}
